/**
 * VikarTabBar — mobile-only bottom tab bar for the VIKAR portal.
 * Hjem / Vagter / Timer / Profil
 * Active color: #8DD9BF. Safe-area aware.
 * Only renders on screens narrower than lg (1024px) — mirrors BottomNav.
 */
import { NavLink, useLocation } from 'react-router-dom';
import { Home, Calendar, Clock, User } from 'lucide-react';
import { haptic } from '../../lib/haptics';

const VIKAR_TABS = [
  { label: 'Hjem', path: '/dashboard/vikar', icon: Home },
  { label: 'Vagter', path: '/mine-vagter', icon: Calendar },
  { label: 'Timer', path: '/my-timesheets', icon: Clock },
  { label: 'Profil', path: '/profile', icon: User },
] as const;

const ACTIVE_COLOR = '#8DD9BF';

// Explicit set of path prefixes where the vikar tab bar should appear.
// Using a fixed list avoids the overly-broad prefix matching that caused
// the tab bar to appear on /dashboard/admin etc.
const VIKAR_PATH_PREFIXES = [
  '/dashboard/vikar',
  '/mine-vagter',
  '/my-timesheets',
  '/profile',
  '/find-vagter',
  '/vikar/',
  '/indstillinger',
  '/mine-lonsedler',
];

export function VikarTabBar() {
  const location = useLocation();

  const isVikarRoute = VIKAR_PATH_PREFIXES.some((prefix) =>
    location.pathname === prefix || location.pathname.startsWith(prefix + '/')
  ) || location.pathname === '/profile';
  if (!isVikarRoute) return null;

  return (
    <nav
      aria-label="Vikar navigering"
      className="fixed bottom-0 inset-x-0 lg:hidden z-[9998] pointer-events-none"
      style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
    >
      <div className="pointer-events-auto bg-[rgba(249,249,251,0.96)] backdrop-blur-[24px] border-t border-gray-200 flex items-stretch justify-around px-2">
        {VIKAR_TABS.map(({ label, path, icon: Icon }) => {
          const isActive =
            location.pathname === path ||
            (path !== '/profile' && location.pathname.startsWith(path + '/'));
          return (
            <NavLink
              key={path}
              to={path}
              onClick={() => haptic('light')}
              className="flex flex-col items-center justify-center gap-0.5 flex-1 py-2.5 min-h-[49px] touch-manipulation relative"
              aria-current={isActive ? 'page' : undefined}
            >
              {/* Active indicator */}
              {isActive && (
                <span
                  className="absolute top-0 left-1/2 -translate-x-1/2 h-[2px] w-8 rounded-full"
                  style={{ background: ACTIVE_COLOR }}
                />
              )}
              <Icon
                strokeWidth={isActive ? 2 : 1.5}
                className="w-[22px] h-[22px] transition-colors"
                style={{ color: isActive ? ACTIVE_COLOR : 'rgba(107,114,128,0.8)' }}
                aria-hidden
              />
              <span
                className="text-[10px] font-medium leading-tight transition-colors"
                style={{ color: isActive ? ACTIVE_COLOR : 'rgba(107,114,128,0.8)' }}
              >
                {label}
              </span>
            </NavLink>
          );
        })}
      </div>
    </nav>
  );
}
